import React from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import { ArrowCaret } from '@fs/zion-icon'
import navigationItemPropType from './navigationItemPropType'
import { visuallyHiddenCss } from '../../styles/visuallyHidden'
import {
  CORNER_NAV_HEADER_TOP_CSS,
  EDGE_TAB_PLACEMENT,
  TOP_CORNER_PLACEMENT,
  CORNER_NAV_RIGHT_CSS,
  CORNER_NAV_SIZE_CSS,
  CORNER_NAV_TOP_CSS,
  NAV_SURFACE_BACKGROUND,
  NAV_SURFACE_COLOR,
  NAV_STEP_BUTTON_INLINE_SIZE_PX,
  focusRingCss,
  NAV_SURFACE_HOVER_OVERLAY,
  NAV_SURFACE_SHADOW,
  NAV_SURFACE_Z_INDEX,
} from './experienceNavSurface.styles'
import { experienceNavControlProps } from './experienceNavControlMarker'

// Larger than the pill is tall, so the browser clamps it to a full semicircle.
const STEP_PILL_RADIUS = '200px'

// The pair mirrors the trigger across the screen, so it takes the trigger's
// own line: centered opposite the STATION edge tab, and on the WEB menu
// button's row otherwise. Centered on that row rather than sharing its top
// edge, so the two read as one line however the control height is tuned. It
// stays on the physical right edge in RTL for the same reason the tab stays
// on the physical left - they are one set of controls framing the
// experience, not text - so `right` is deliberate here.
/* eslint-disable @fs/zion/logical-over-directional */
const cornerLineCss = (headerVisible) =>
  `calc(${headerVisible ? CORNER_NAV_HEADER_TOP_CSS : CORNER_NAV_TOP_CSS} + ${CORNER_NAV_SIZE_CSS} / 2)`

const stepButtonsCss = (isEdgeTab, headerVisible) => css`
  /* A fieldset so the legend names the pair natively; strip the UA border,
     padding and min-content min-inline-size so it lays out like a plain box. */
  border: 0;
  padding: 0;
  margin: 0;
  min-inline-size: 0;

  position: fixed;
  top: ${isEdgeTab ? '50%' : cornerLineCss(headerVisible)};
  right: ${CORNER_NAV_RIGHT_CSS};
  z-index: ${NAV_SURFACE_Z_INDEX};
  transform: translateY(-50%);

  /* One joined pill, the same browser-style cluster the time machine uses for
     its person history - two floating circles read as two more things on
     screen, which is the opposite of what a stepper this incidental wants. */
  display: inline-flex;
  align-items: center;
  border-radius: ${STEP_PILL_RADIUS};
  background: ${NAV_SURFACE_BACKGROUND};
  color: ${NAV_SURFACE_COLOR};
  box-shadow: ${NAV_SURFACE_SHADOW};
  overflow: hidden;
`
/* eslint-enable @fs/zion/logical-over-directional */

const stepButtonCss = css`
  box-sizing: border-box;
  width: ${NAV_STEP_BUTTON_INLINE_SIZE_PX}px;
  height: 30px;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover,
  &:focus {
    background: ${NAV_SURFACE_HOVER_OVERLAY};
  }

  ${focusRingCss};

  /* Each button takes the pill's curve on its own outer side, so the inset
     focus ring follows the pill instead of drawing a square box whose outer
     half the pill's overflow clips away (DSC-10721). Logical corners, so the
     curve stays on the outside when RTL flips the pair's order. */
  &:first-of-type {
    border-start-start-radius: ${STEP_PILL_RADIUS};
    border-end-start-radius: ${STEP_PILL_RADIUS};
  }

  &:last-of-type {
    border-start-end-radius: ${STEP_PILL_RADIUS};
    border-end-end-radius: ${STEP_PILL_RADIUS};
  }

  & + & {
    border-inline-start: 1px solid rgba(0, 0, 0, 0.12);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/**
 * Steps through the same destinations the navigation card lists, without
 * opening it — the common move inside a section is "the next one", and on a
 * kiosk that shouldn't cost two taps and a full-screen overlay.
 *
 * Rendered only where the card knows which row the visitor is on (see
 * ExperienceNavControls): with no anchor in the list there is no next. The
 * list wraps, so both arrows always lead somewhere and neither ever goes
 * dead under a finger already reaching for it.
 */
export default function ExperienceNavStepButtons({
  triggerPlacement,
  headerVisible,
  groupLabel,
  previousItem,
  nextItem,
  previousLabel,
  nextLabel,
  onSelect,
}) {
  return (
    <fieldset
      css={stepButtonsCss(triggerPlacement === EDGE_TAB_PLACEMENT, headerVisible)}
      {...experienceNavControlProps}
    >
      <legend css={visuallyHiddenCss}>{groupLabel}</legend>
      <button type="button" css={stepButtonCss} onClick={() => onSelect(previousItem)} aria-label={previousLabel}>
        <ArrowCaret direction="backward" size="sm" aria-hidden />
      </button>
      <button type="button" css={stepButtonCss} onClick={() => onSelect(nextItem)} aria-label={nextLabel}>
        <ArrowCaret direction="forward" size="sm" aria-hidden />
      </button>
    </fieldset>
  )
}

ExperienceNavStepButtons.propTypes = {
  triggerPlacement: PropTypes.oneOf([EDGE_TAB_PLACEMENT, TOP_CORNER_PLACEMENT]).isRequired,
  headerVisible: PropTypes.bool.isRequired,
  /**
   * Names what the two arrows step through - the section, where there is one.
   * Rendered as the fieldset's visually hidden legend, so the pair is named
   * natively rather than via a role="group" div.
   */
  groupLabel: PropTypes.string.isRequired,
  /** The row before the current one, wrapping to the last from the first. */
  previousItem: navigationItemPropType.isRequired,
  /** The row after the current one, wrapping to the first from the last. */
  nextItem: navigationItemPropType.isRequired,
  previousLabel: PropTypes.string.isRequired,
  nextLabel: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
}
